import { matchPath } from "react-router-dom";

/**
 * Which help entry belongs to which route.
 *
 * The header's help button is one control for the whole console, so the page
 * it explains is decided from the URL rather than by each page mounting its
 * own trigger. Patterns are the route table's own paths — the guard test
 * fails on a pattern the table does not have, and on a help entry no pattern
 * reaches.
 *
 * Order matters only where patterns overlap, and none here do: every pattern
 * is exact (matchPath, `end: true`), so `/scans` and `/scans/:runId` are
 * different entries rather than a prefix and its children.
 *
 * The tenant patterns carry the `/t/:tenant` prefix the route table gives them.
 * A pattern written without it would match nothing, and the help button would
 * silently disappear from every page in the console rather than fail loudly.
 */
export const HELP_ROUTES: readonly (readonly [pattern: string, page: string])[] = [
  // PLATFORM pages. They belong to the deployment, so they carry no tenant prefix.
  ["/tenants", "tenants_overview"],
  ["/tenants/manage", "tenants"],
  ["/admin/users", "users"],
  ["/admin/audit", "audit"],
  ["/admin/platform", "platform_settings"],

  // The tenant console. Every one of these lives under /t/:tenant, and the
  // pattern must say so or the help button goes quiet on every page.
  ["/t/:tenant/overview", "overview"],
  ["/t/:tenant/inventory", "inventory"],
  ["/t/:tenant/inventory/:kind", "inventory"],
  ["/t/:tenant/inventory/:kind/:id", "inventory_detail"],
  ["/t/:tenant/posture", "posture"],
  ["/t/:tenant/posture/critical-high", "posture"],
  ["/t/:tenant/posture/medium-low", "posture"],
  ["/t/:tenant/posture/all", "posture"],
  ["/t/:tenant/posture/baseline", "posture_baseline"],
  ["/t/:tenant/posture/secure-score", "posture"],
  ["/t/:tenant/posture/ai-bp", "posture_ai_bp"],
  ["/t/:tenant/posture/manual", "posture"],
  ["/t/:tenant/posture/exceptions", "posture"],
  ["/t/:tenant/posture/check/:checkId", "check_detail"],
  ["/t/:tenant/defender", "defender"],
  ["/t/:tenant/changes", "changes"],
  ["/t/:tenant/history", "history"],
  ["/t/:tenant/scans", "scans"],
  ["/t/:tenant/scans/:runId", "scan_detail"],
  ["/t/:tenant/reports", "reports"],
  ["/t/:tenant/admin/settings/tenant-connection", "tenant_connection"],
  ["/t/:tenant/admin/settings/scanning", "settings"],
  ["/t/:tenant/admin/settings/scoring", "settings"],
  ["/t/:tenant/admin/settings/thresholds", "settings"],
  ["/t/:tenant/admin/settings/retention", "settings"],
  ["/t/:tenant/admin/settings/reporting", "settings"],
  ["/t/:tenant/admin/settings/preview", "settings"],
  ["/t/:tenant/admin/settings/modules", "modules"],
  ["/t/:tenant/admin/settings/emergency-access", "emergency_access"],
];

/** The help page id for a pathname, or undefined when the page has none. */
export function helpPageForPath(pathname: string): string | undefined {
  for (const [pattern, page] of HELP_ROUTES) {
    if (matchPath({ path: pattern, end: true }, pathname) !== null) return page;
  }
  return undefined;
}
